/**
 * Location Configuration Page
 * Manage operational hours and alert settings for all locations
 */

import { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useApiRequest from "utils/Apirequest";
import { useMaterialUIController } from "context";
import CustomMaterialTablePage from "components/custom/CustomMaterialTablePage";
import Loader from "components/custom/Loader";
import OperationalHoursModal from "./OperationalHoursModal";

const LocationConfigPage = () => {
  const apiRequest = useApiRequest();
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([
    { id: "locationName", desc: false },
  ]);
  const [searchText, setSearchText] = useState("");

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        sortField: sorting[0]?.id || "locationName",
        sortOrder: sorting[0]?.desc ? "desc" : "asc",
        search: searchText,
      });

      const { data } = await apiRequest(
        "get",
        `locations?${params.toString()}`,
        null,
        {},
        {}
      );

      if (data?.success) {
        // Debug: Log the actual API response structure
        console.log('API Response:', data);
        console.log('Sample location data:', data.data?.[0]);
        
        // Map the API response to match table structure
        const mappedLocations = (data.data || []).map(location => ({
          ...location,
          locationName: location.name || location.locationName || location.title || 'N/A',
          address: location.address || location.fullAddress || location.location || 'N/A',
          operationalHours: location.operationalHours || location.operational_hours || null
        }));
        setLocations(mappedLocations);
        setTotalRows(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, sorting, searchText]);

  const handleConfigureClick = (location) => {
    setSelectedLocation(location);
    setConfigModalOpen(true);
  };

  const handleConfigSave = () => {
    fetchLocations();
    setConfigModalOpen(false);
    setSelectedLocation(null);
  };

  const getStatusIcon = (location) => {
    if (!location.operationalHours) {
      return (
        <Tooltip title="Not Configured">
          <WarningIcon color="warning" />
        </Tooltip>
      );
    }
    if (location.operationalHours.enabled) {
      return (
        <Tooltip title="Enabled">
          <CheckCircleIcon color="success" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title="Disabled">
        <CancelIcon color="error" />
      </Tooltip>
    );
  };

  const columns = [
    {
      header: "Status",
      accessorKey: "status",
      size: 100,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => getStatusIcon(row.original),
    },
    {
      header: "Location Name",
      accessorKey: "locationName",
      size: 250,
      Cell: ({ cell }) => (
        <MDTypography variant="button" fontWeight="medium">
          {cell.getValue()}
        </MDTypography>
      ),
    },
    {
      header: "Address",
      accessorKey: "address",
      size: 300,
      Cell: ({ cell }) => (
        <MDTypography variant="caption" color="text">
          {cell.getValue() || "N/A"}
        </MDTypography>
      ),
    },
    {
      header: "Operational Hours",
      accessorKey: "operationalHours",
      size: 200,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const hours = row.original.operationalHours;
        if (!hours || !hours.enabled) {
          return (
            <Chip label="Not Set" size="small" variant="outlined" color="default" />
          );
        }
        return (
          <MDTypography variant="caption">
            {hours.startTime} - {hours.endTime}
          </MDTypography>
        );
      },
    },
    {
      header: "Timezone",
      accessorKey: "timezone",
      size: 150,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const hours = row.original.operationalHours;
        return (
          <MDTypography variant="caption">
            {hours?.timezone || "N/A"}
          </MDTypography>
        );
      },
    },
    {
      header: "Alert Delay",
      accessorKey: "alertDelay",
      size: 120,
      align: "center",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const hours = row.original.operationalHours;
        return (
          <Chip
            label={hours?.alertDelay ? `${hours.alertDelay} min` : "N/A"}
            size="small"
            color={hours?.alertDelay ? "primary" : "default"}
          />
        );
      },
    },
    {
      header: "Configuration",
      accessorKey: "config",
      size: 150,
      align: "center",
      enableSorting: false,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <Tooltip title="Configure Operational Hours">
          <IconButton
            size="small"
            onClick={() => handleConfigureClick(row.original)}
            color="primary"
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h4" fontWeight="medium">
                  Location Configuration
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  Configure operational hours and alert settings for each location
                </MDTypography>
              </MDBox>
            </Card>
          </Grid>

          {/* Locations Table */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  All Locations
                </MDTypography>
              </MDBox>

              {loading ? (
                <Loader />
              ) : (
                <CustomMaterialTablePage
                  columns={columns}
                  data={locations}
                  darkMode={darkMode}
                  manualPagination={true}
                  rowCount={totalRows}
                  paginationChange={setPagination}
                  soringChange={setSorting}
                  page={pagination.pageIndex}
                  pagesize={pagination.pageSize}
                  paginataion={pagination}
                  sorting={sorting}
                  searchText={searchText}
                  searchChange={setSearchText}
                  state={{ pagination, sorting }}
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Operational Hours Modal */}
      {selectedLocation && (
        <OperationalHoursModal
          open={configModalOpen}
          onClose={() => {
            setConfigModalOpen(false);
            setSelectedLocation(null);
          }}
          locationId={selectedLocation._id}
          config={selectedLocation.operationalHours}
          onSave={handleConfigSave}
        />
      )}
    </DashboardLayout>
  );
};

export default LocationConfigPage;
